import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import { SaveOutlined, UploadOutlined, SettingOutlined, PlusOutlined, DeleteOutlined, DragOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import ProductSelect from './ProductSelect';
import ShelfSelectImpl from '../selects/ShelfSelect';
import MerchantResetButtonImpl from '@components/operator/MerchantResetButton';
import StorageModeContext from '@contexts/operator/StorageModeContext';
import { useTour } from '@contexts/TourContext';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import useSmartBack from '@hooks/shared/useSmartBack';
import PageHeader from '@shared/PageHeader';
import Can from '@shared/permissions/Can';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import adminApiFetch from '@services/admin/apiService';
import operatorApiFetch from '@services/operator/apiService';
import {
  Form,
  Input,
  Button,
  Card,
  message,
  Space,
  Collapse,
  Row,
  Col,
  Select,
  InputNumber,
  Upload,
  Spin,
  Switch,
  Alert,
  Divider,
  Typography,
  Tag,
  Checkbox,
  Empty,
  Dropdown
} from 'antd';
import type { FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router-dom';
const { TextArea } = Input;
const { Panel } = Collapse;

type Role = 'admin' | 'operator';

type TFunc = ReturnType<typeof useTranslation>['t'];

interface TourRefs {
  saveButtonRef: React.RefObject<HTMLElement | null>;
  nameRef: React.RefObject<HTMLDivElement | null>;
  skuRef: React.RefObject<HTMLDivElement | null>;
  temperatureRef: React.RefObject<HTMLDivElement | null>;
  validityDaysRef: React.RefObject<HTMLDivElement | null>;
  largeUnitSmallQuantityRef: React.RefObject<HTMLDivElement | null>;
}

interface ProductTourControllerProps {
  form: FormInstance;
  registerTour: (steps: unknown[]) => void;
  refs: TourRefs;
  t: TFunc;
}

interface PalletConfigFieldsProps {
  form: FormInstance;
  t: TFunc;
}

interface UseProductServiceOpts {
  merchantId?: string;
}

// useAdminProductService 仍是 .js (JSDoc `@returns {Object}` → TS 推成 `Object`)，
// 而 useOperatorProductService 是 typed .ts。為了讓兩個 hook 都能滿足同一個 prop 型別，
// 這個 boundary 故意收成 `any` —— 後端 service 物件 method 介面在元件內部使用。
type UseProductServiceFn = (opts?: UseProductServiceOpts) => any;

interface ProductLoadedPayload {
  merchant_id?: string | null;
  merchant?: { id?: string | null; name?: string | null } | null;
  [key: string]: unknown;
}

interface ProductEditProps {
  useProductService: UseProductServiceFn;
  role?: Role;
  // 在 operator 編輯模式下，wrapper 由 useRecordMerchantSync 解析出 record 的 merchant_id
  // 後傳進來；新增模式則由 MerchantSelect 傳入使用者選的 merchant。Admin 不需要。
  merchantId?: string | null;
  // wrapper 在 GET show response 拿到 product 後呼叫，把 OperatorDataContext.currentMerchant
  // 對齊到該 record 的 merchant，避免在另一個 merchant 的下拉清單上 render 出 raw UUID。
  onProductLoaded?: (product: ProductLoadedPayload) => void;
  // wrapper 還在把 currentMerchant 切到 record's merchant 時為 true；render-gate
  // 用，避免 form 短暫顯示前一個 merchant 的 brand / inventory_type 選項。
  merchantDataLoading?: boolean;
}

interface SkuValidationState {
  status: 'idle' | 'checking' | 'valid' | 'invalid';
  message: string;
}

interface ShelfOption {
  value: string;
  label: string;
  location?: string;
  warehouse?: unknown;
  temperature?: unknown;
}

/**
 * Tour 控制器組件 - 獨立處理 Tour 邏輯，避免 watch 導致主表單 re-render
 */
const ProductTourController: React.FC<ProductTourControllerProps> = ({ form, registerTour, refs, t }) => {
  const skuValue = Form.useWatch('sku', form);
  const nameValue = Form.useWatch('name', form);

  const renderStepContent = (descriptionKey: string, value: unknown) => (
    <div>
      <div>{t(descriptionKey)}</div>
      {!value && (
        <div style={{ color: '#ff4d4f', marginTop: 8, fontSize: '12px' }}>
          {t('admin.products.tour.fill_to_continue')}
        </div>
      )}
    </div>
  );

  useEffect(() => {
    const steps = [
      {
        title: t('admin.products.tour.name.title'),
        description: renderStepContent('admin.products.tour.name.description', nameValue),
        target: () => refs.nameRef.current,
        nextButtonProps: { disabled: !nameValue },
      },
      {
        title: t('admin.products.tour.sku.title'),
        description: renderStepContent('admin.products.tour.sku.description', skuValue),
        target: () => refs.skuRef.current,
        nextButtonProps: { disabled: !skuValue },
      },
      {
        title: t('admin.products.tour.temperature.title'),
        description: t('admin.products.tour.temperature.description'),
        target: () => refs.temperatureRef.current,
      },
      {
        title: t('admin.products.tour.validity_days.title'),
        description: t('admin.products.tour.validity_days.description'),
        target: () => refs.validityDaysRef.current,
      },
      {
        title: t('admin.products.tour.unit_large_small_quantity.title'),
        description: t('admin.products.tour.unit_large_small_quantity.description'),
        target: () => refs.largeUnitSmallQuantityRef.current,
      },
      {
        title: t('admin.products.tour.save.title'),
        description: t('admin.products.tour.save.description'),
        target: () => refs.saveButtonRef.current,
      }
    ];

    registerTour(steps);
    return () => registerTour([]);
  }, [registerTour, t, skuValue, nameValue, refs]);

  return null; // 不渲染任何 UI
};

/**
 * PalletConfigFields - 棧板配置欄位
 * Total 為主角（系統計算依據），Ti/Hi 為輔助堆疊描述
 * 獨立 watch 避免主表單 re-render
 */
const PalletConfigFields: React.FC<PalletConfigFieldsProps> = ({ form, t }) => {
  const largeUnitPerLayer = Form.useWatch('large_unit_per_layer', form);
  const layersPerPallet = Form.useWatch('layers_per_pallet', form);
  const totalLargeUnitsPerPallet = Form.useWatch('total_large_units_per_pallet', form);
  const prevStandardRef = useRef<number | null>(null);

  const standardQty: number | null = (largeUnitPerLayer > 0 && layersPerPallet > 0)
    ? largeUnitPerLayer * layersPerPallet
    : null;

  // Auto-fill total when Ti or Hi changes
  useEffect(() => {
    if (standardQty === null) return;

    const currentTotal = form.getFieldValue('total_large_units_per_pallet');
    // Auto-fill if: no current value, or current value matched previous standard (not manually overridden)
    if (!currentTotal || currentTotal === prevStandardRef.current) {
      form.setFieldValue('total_large_units_per_pallet', standardQty);
    }
    prevStandardRef.current = standardQty;
  }, [standardQty, form]);

  const isOverridden = standardQty !== null
    && totalLargeUnitsPerPallet != null
    && totalLargeUnitsPerPallet !== standardQty;

  const handleReset = () => {
    if (standardQty !== null) {
      form.setFieldValue('total_large_units_per_pallet', standardQty);
    }
  };

  return (
    <>
      {/* 主欄位: Total */}
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12} md={6}>
          <Form.Item
            label={
              <Space size={4}>
                <span>{t(`models.product.total_large_units_per_pallet`)}</span>
                {isOverridden && (
                  <Tag color="orange" style={{ marginRight: 0, lineHeight: '18px', height: 20, fontSize: 11 }}>
                    {t('shared.products.form.pallet_overridden')}
                  </Tag>
                )}
              </Space>
            }
            name="total_large_units_per_pallet"
            tooltip={t(`shared.products.form.total_large_units_per_pallet_tooltip`)}
          >
            <InputNumber data-testid="product-total-large-per-pallet-input" min={1} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      {/* 輔助區: Ti / Hi 堆疊方式 */}
      <div style={{
        background: '#fafafa',
        borderRadius: 6,
        padding: '12px 16px',
        border: '1px solid #f0f0f0',
      }}>
        <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {t('shared.products.form.pallet_stacking_section')}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            — {t('shared.products.form.pallet_stacking_hint')}
          </Typography.Text>
        </div>
        <Row gutter={[16, 0]} align="middle">
          <Col xs={10} sm={8} md={5}>
            <Form.Item
              label={t(`models.product.large_unit_per_layer`)}
              name="large_unit_per_layer"
              tooltip={t(`shared.products.form.large_unit_per_layer_tooltip`)}
              style={{ marginBottom: 0 }}
            >
              <InputNumber data-testid="product-large-unit-per-layer-input" min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col flex="none" style={{ paddingBottom: 4 }}>
            <Typography.Text type="secondary">×</Typography.Text>
          </Col>
          <Col xs={10} sm={8} md={5}>
            <Form.Item
              label={t(`models.product.layers_per_pallet`)}
              name="layers_per_pallet"
              tooltip={t(`shared.products.form.layers_per_pallet_tooltip`)}
              style={{ marginBottom: 0 }}
            >
              <InputNumber data-testid="product-layers-per-pallet-input" min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          {standardQty !== null && (
            <Col flex="none" style={{ paddingBottom: 4 }}>
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                = {standardQty}
              </Typography.Text>
            </Col>
          )}
        </Row>
        {isOverridden && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tag color="orange" style={{ margin: 0 }}>
              {t('shared.products.form.pallet_stacking_mismatch', { actual: totalLargeUnitsPerPallet })}
            </Tag>
            <Typography.Link onClick={handleReset} style={{ fontSize: 12 }}>
              {t('shared.products.form.pallet_reset')}
            </Typography.Link>
          </div>
        )}
      </div>
    </>
  );
};

/**
 * 通用產品編輯視圖組件
 *
 * Operator 路徑下的 merchant 對齊由 wrapper 透過 `onProductLoaded` +
 * `merchantDataLoading` + `merchantId` 三個 prop 控制；本元件不直接讀 / 寫
 * OperatorDataContext，保持 merchant-agnostic 以便 admin 共用。
 */
const ProductEdit: React.FC<ProductEditProps> = ({
  useProductService,
  role = 'admin',
  merchantId = null,
  onProductLoaded,
  merchantDataLoading = false,
}) => {
  // ShelfSelect / MerchantResetButton 仍是 .jsx，TS 從其推導出的 props 不完整。
  // 用 ComponentType<any> 包一層以保留原 runtime 行為；放在 component body 內避免 Vite HMR top-level TDZ。
  const ShelfSelect = ShelfSelectImpl as React.ComponentType<any>;
  const MerchantResetButton = MerchantResetButtonImpl as React.ComponentType<any>;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const { id } = useParams();
  const isNew = !id;

  const [form] = Form.useForm();

  // 安全讀取 storage_mode：useStorageMode() 在沒有 Provider 時會 throw（admin 樹未掛 Provider），
  // 因此這裡直接用 useContext 讀原 context — 沒掛 Provider 時為 null，視為「不渲染儲位欄位」。
  // 此元件 admin / operator 共用，僅 operator 會掛 StorageModeProvider。
  const storageModeCtx = React.useContext(StorageModeContext) as { isDynamic?: boolean; isDedicated?: boolean } | null;
  const isStorageModeDynamic = !!storageModeCtx?.isDynamic;
  const isStorageModeDedicated = !!storageModeCtx?.isDedicated;

  // Safe tour context
  let tourContext: { registerTour?: (steps: unknown[]) => void } = {};
  try {
    tourContext = useTour() as { registerTour?: (steps: unknown[]) => void };
  } catch (e) {
    // ignore
  }
  const { registerTour } = tourContext;

  const saveButtonRef = useRef<HTMLElement | null>(null);
  const nameRef = useRef<HTMLDivElement | null>(null);
  const skuRef = useRef<HTMLDivElement | null>(null);
  const temperatureRef = useRef<HTMLDivElement | null>(null);
  const validityDaysRef = useRef<HTMLDivElement | null>(null);
  const largeUnitSmallQuantityRef = useRef<HTMLDivElement | null>(null);

  // Tour refs 對象，用 useMemo 穩定引用，避免無限循環
  const tourRefs = useMemo<TourRefs>(() => ({
    saveButtonRef,
    nameRef,
    skuRef,
    temperatureRef,
    validityDaysRef,
    largeUnitSmallQuantityRef,
  }), []);

  const [loading, setLoading] = useState(false);
  // 區分「儲存並新增下一筆」的 loading，避免兩顆按鈕同時轉 spinner
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  // SKU validation state: 'idle' | 'checking' | 'valid' | 'invalid'
  const [skuValidation, setSkuValidation] = useState<SkuValidationState>({ status: 'idle', message: '' });
  const lastCheckedSkuRef = useRef<string | null>(null);
  // 從 product 載入的 merchant_id（給替代品搜尋等 fallback 用，
  // 例如 wrapper 還沒同步 context 之前的暫態）。Merchant 對齊邏輯由 wrapper 的
  // useRecordMerchantSync 處理，shared 自己不再讀 / 寫 OperatorDataContext。
  const [loadedMerchantId, setLoadedMerchantId] = useState<string | null>(null);
  // Call hook with merchantId parameter - useProductService handles role internally
  const service = useProductService(role === 'operator' ? { merchantId: merchantId ?? undefined } : undefined);
  // Options data and loading states
  const [brands, setBrands] = useState<Array<{ label: string; value: unknown }>>([]);
  const [temperatures, setTemperatures] = useState<Array<{ label: string; value: string }>>([]);
  const [categories, setCategories] = useState<Array<{ label: string; value: unknown }>>([]);
  const [productAttributeDefinitions, setProductAttributeDefinitions] = useState<any[]>([]);
  // EC 同步：此商品所屬 merchant 中「啟用同步」的電商帳號全列表（候選名單，供「新增例外」下拉用）
  const [ecPushAccountCandidates, setEcPushAccountCandidates] = useState<Array<{
    ec_account_id: string;
    ec_account_name: string;
    ec_account_module_name?: string;
    account_stock_percentage?: number;
    inventory_type_name?: string | null;
    secondary_inventory_type_name?: string | null;
  }>>([]);
  const [initialShelfOption, setInitialShelfOption] = useState<ShelfOption | null>(null);
  const [optionsLoading, setOptionsLoading] = useState({
    brands: false,
    categories: false
  });
  const [optionsLoaded, setOptionsLoaded] = useState({
    brands: false,
    categories: false
  });
  // Generic select filter function
  const filterOption = useCallback((input: string, option: any) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
    []
  );
  // Generic options loader
  const loadOptions = useCallback(async (type: 'brands' | 'categories', fetchFn: () => Promise<any>) => {
    if (optionsLoaded[type] || optionsLoading[type]) return;
    try {
      setOptionsLoading(prev => ({ ...prev, [type]: true }));
      const data = await fetchFn();
      if (type === 'brands') setBrands(data || []);
      if (type === 'categories') setCategories(data || []);
      setOptionsLoaded(prev => ({ ...prev, [type]: true }));
    } catch (error) {
      console.error(`Load ${type} error:`, error);
    } finally {
      setOptionsLoading(prev => ({ ...prev, [type]: false }));
    }
  }, [optionsLoaded, optionsLoading]);
  const loadBrands = useCallback(() =>
    loadOptions('brands', service.getBrands),
    [loadOptions, service]
  );
  const loadCategories = useCallback(() =>
    loadOptions('categories', service.getCategories),
    [loadOptions, service]
  );
  useEffect(() => {
    loadData();
  }, [id]);

  // 新建模式：當 merchantId 變更時，重新載入 product_attribute_definitions
  useEffect(() => {
    if (!id && role === 'operator' && merchantId) {
      const loadAttributeDefinitions = async () => {
        try {
          const defsResponse = await operatorApiFetch(`product_attribute_definitions?merchant_id=${merchantId}`);
          if (defsResponse.ok) {
            const defsData = await defsResponse.json();
            setProductAttributeDefinitions(defsData.data || []);
          }
        } catch (error) {
          console.error('Load attribute definitions error:', error);
        }
      };
      loadAttributeDefinitions();
    }
  }, [id, role, merchantId]);

  // 多貨主場景：當 merchant 上下文變動（建立流程切換貨主）時，
  // 失效 brands 快取，下次 Select onFocus 會以新 merchantId 重新拉取，避免下拉混入其他貨主品牌。
  // 不清空 brands 陣列，保留編輯模式 pre-populate 的選項顯示，避免 label 暫時消失。
  // 同步清空 form.brand_id：避免使用者切貨主後送出屬於前一貨主的 brand_id（資料一致性）。
  //
  // 只有「從一個已 settle 的 merchantId 切到另一個已 settle 的 merchantId」才該觸發 invalidation。
  // 用 ref 記下第一個 settle 下來的值（穩定態 = merchantDataLoading false 且 merchantId truthy），
  // 後續才比對是否真的「切換」；如此可同時排除：
  //   (a) mount 那拍 merchantId 還是 null。
  //   (b) 編輯模式 useRecordMerchantSync 把 merchantId 從 null → record's merchant 的 sync 過渡。
  //   (c) sync 完成那拍 merchantDataLoading false 自身翻轉觸發 effect re-run（不要把剛載入的
  //       brand_id 默默清掉 — 這是 PR #1114 review 抓到的 race）。
  const lastSettledMerchantIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (role !== 'operator') return;
    if (merchantDataLoading) return;
    if (!merchantId) return;
    const previous = lastSettledMerchantIdRef.current;
    if (previous === merchantId) return;
    lastSettledMerchantIdRef.current = merchantId;
    if (previous === null) return; // 第一次 settle 不算切換
    setOptionsLoaded(prev => (prev.brands ? { ...prev, brands: false } : prev));
    form.setFieldsValue({ brand_id: undefined });
  }, [role, merchantId, form, merchantDataLoading]);

  const loadData = async () => {
    try {
      setDataLoading(true);

      // Load temperatures (constants, no API)
      const temperaturesData = await service.getTemperatures();
      const tempOptions = temperaturesData.map((temp: string) => ({
        label: t(`common.shelf_temperature.${temp}`) as string,
        value: temp
      }));
      setTemperatures(tempOptions || []);

      // Load product data if editing
      if (id) {
        const response = await service.getById(id);
        if (response.success) {
          const product = response.data;

          // 通知 wrapper：拿到 record 後讓它把 OperatorDataContext.currentMerchant
          // 對齊到該 record 的 merchant（由 useRecordMerchantSync 處理）。Shared 本身
          // 不再讀 / 寫 merchant context，保持 merchant-agnostic。
          onProductLoaded?.(product);

          // 方案 A: 從 Product API 返回取得 product_attribute_definitions（編輯模式）
          if (product.product_attribute_definitions) {
            setProductAttributeDefinitions(product.product_attribute_definitions);
          }

          // EC 同步：保存所有啟用同步的電商帳號作為「新增例外」下拉候選
          setEcPushAccountCandidates(product.ec_push_account_options || []);

          // 記錄產品的 merchant_id（供替代品搜尋等 fallback 用途）
          if (product.merchant_id) {
            setLoadedMerchantId(product.merchant_id);
          }
          // Pre-populate brand and category options if exists
          if (product.brand_id && product.brand?.name) {
            setBrands([{ label: product.brand.name, value: product.brand_id }]);
          }
          if (product.product_category_id && product.product_category?.name) {
            setCategories([{ label: product.product_category.name, value: product.product_category_id }]);
          }

          // 設定預設儲位選項
          if (product.default_shelf) {
            setInitialShelfOption({
              value: product.default_shelf.id,
              label: product.default_shelf.location,
              location: product.default_shelf.location,
              warehouse: product.default_shelf.warehouse,
              temperature: product.default_shelf.temperature
            });
          }

          // Prepare photo for Upload component
          let photoFileList: any[] = [];
          // API 回傳的是 public_photo_url（S3 簽名 URL）
          const photoUrl = product.public_photo_url;
          if (photoUrl) {
            photoFileList = [{
              uid: '-1',
              name: 'product-photo',
              status: 'done',
              url: photoUrl
            }];
          }
          // 「需要序號」由 required_serial_numbers_count > 0 推導，沒有獨立 flag。
          const requiredSerialNumbersCount = product.required_serial_numbers_count || 0;
          setNeedSerialNumber(requiredSerialNumbersCount > 0);

          // 準備表單值 - 如果品牌不存在，清除 brand_id
          const formValues: any = {
            ...product,
            secondary_barcodes: product.secondary_barcodes?.join('\n') || '',
            photo: photoFileList,
            required_serial_numbers_count: requiredSerialNumbersCount,
            // 明確設置布爾值，確保 Switch 組件正確顯示
            batch_management_required: !!product.batch_management_required,
            manufacturing_date_required: !!product.manufacturing_date_required,
            activated: product.activated !== false, // 預設為 true
            oversea_imported: !!product.oversea_imported,
            ec_stock_push_enabled: product.ec_stock_push_enabled !== false, // 預設為 true
            product_substitutes_attributes: (product.product_substitutes || []).map((sub: any) => ({
              id: sub.id,
              candidate_id: sub.candidate_id,
              priority: sub.priority,
              sku: sub.candidate?.sku,
              name: sub.candidate?.name
            })),
            // EC 同步：只 hydrate 已存在的例外（setting_id 存在的 row）；
            // 其餘啟用同步的帳號保留為「預設行為」(無 row = 用該帳號預設比例推送)
            ec_push_settings_attributes: (product.ec_push_account_options || [])
              .filter((opt: any) => opt.setting_id)
              .map((opt: any) => ({
                id: opt.setting_id,
                ec_account_id: opt.ec_account_id,
                ec_account_name: opt.ec_account_name,
                ec_account_module_name: opt.ec_account_module_name,
                account_stock_percentage: opt.account_stock_percentage,
                inventory_type_name: opt.inventory_type_name,
                secondary_inventory_type_name: opt.secondary_inventory_type_name,
                enabled: opt.enabled !== false,
                // 舊資料若為 nil，FE 顯示為帳號預設值（讓使用者明確覆寫）
                stock_percentage_override: opt.stock_percentage_override ?? opt.account_stock_percentage ?? null
              }))
          };

          // 如果有 brand_id 但沒有 brand 資料，清除 brand_id（品牌可能已被刪除）
          if (product.brand_id && !product.brand) {
            formValues.brand_id = null;
          }

          form.setFieldsValue(formValues);
        } else {
          handleApiError(response, { t, fallbackMessage: t('common.error.record_not_found') });
          navigate('/products');
        }
      } else {
        // 新增商品時設定預設值
        const newProductDefaults: Record<string, unknown> = {
          required_serial_numbers_count: 0
        };
        if (temperaturesData && temperaturesData.length > 0) {
          newProductDefaults.temperature = temperaturesData[0]; // 預設選擇第一個溫層（常溫）
        }
        form.setFieldsValue(newProductDefaults);

        // 新建模式：載入 product_attribute_definitions
        // Operator 需要 merchant_id，由專用 useEffect 在 merchantId 設好後處理
        // Admin 不需要 merchant_id，直接載入
        if (role !== 'operator') {
          const defsResponse = await adminApiFetch('product_attribute_definitions');
          if (defsResponse.ok) {
            const defsData = await defsResponse.json();
            setProductAttributeDefinitions(defsData.data || []);
          }
        }
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Load data error:', error);
    } finally {
      setDataLoading(false);
    }
  };

  // SKU availability check (on blur)
  const checkSkuAvailability = useCallback(async (sku: string): Promise<{ valid: boolean; message: string }> => {
    if (!sku || sku.trim() === '') {
      setSkuValidation({ status: 'idle', message: '' });
      lastCheckedSkuRef.current = null;
      return { valid: true, message: '' };
    }

    const trimmedSku = sku.trim();

    // Skip if already checked this exact SKU
    if (lastCheckedSkuRef.current === trimmedSku && skuValidation.status === 'valid') {
      return { valid: true, message: '' };
    }

    // 構建 checkSku 選項：
    // - 編輯模式：傳 productId（後端會自動取得 merchant 並排除自身）
    // - 新增模式 (operator)：傳 merchantId
    // - 新增模式 (admin)：不需額外參數（token 已含 merchant）
    const checkSkuOptions: { productId?: string; merchantId?: string } = {};
    if (id) {
      // 編輯模式
      checkSkuOptions.productId = id;
    } else if (role === 'operator' && merchantId) {
      // Operator 新增模式
      checkSkuOptions.merchantId = merchantId;
    }

    setSkuValidation({ status: 'checking', message: '' });
    try {
      const response = await service.checkSku(trimmedSku, checkSkuOptions);
      if (response.success) {
        if (response.data?.exists) {
          const errorMessage = t(`shared.products.form.sku_already_exists`, { sku: trimmedSku, name: response.data.name }) as string;
          setSkuValidation({ status: 'invalid', message: errorMessage });
          return { valid: false, message: errorMessage };
        } else {
          setSkuValidation({ status: 'valid', message: '' });
          lastCheckedSkuRef.current = trimmedSku;
          return { valid: true, message: '' };
        }
      } else {
        setSkuValidation({ status: 'idle', message: '' });
        handleApiError(response, { t });
        return { valid: true, message: '' }; // Allow submit on API error
      }
    } catch (error) {
      setSkuValidation({ status: 'idle', message: '' });
      handleNetworkError(error, { t });
      console.error('Check SKU error:', error);
      return { valid: true, message: '' }; // Allow submit on network error
    }
  }, [id, t, service, role, merchantId, skuValidation.status]);

  // 組合送出 payload，給兩個提交按鈕共用；photo 需 async 轉 base64
  const buildPayload = async (values: any) => {
    // Process secondary_barcodes and priorities
    const processedValues: any = {
      ...values,
      // Defensive dedupe: split lines, trim, drop blanks, then unique.
      // Backend also dedupes + validates uniqueness, but de-duplicating here
      // avoids unnecessary 422s when users accidentally enter the same
      // barcode twice (e.g. via copy/paste).
      secondary_barcodes: values.secondary_barcodes
        ? Array.from(
            new Set(
              values.secondary_barcodes
                .split('\n')
                .map((v: string) => v.trim())
                .filter(Boolean)
            )
          )
        : [],
      product_substitutes_attributes: (values.product_substitutes_attributes || []).map((sub: any, index: number) => ({
        ...sub,
        priority: index + 1
      }))
    };

    // count = 0 等同於不追蹤序號；同時把 pattern / delimiter 清空避免殘留無效設定。
    const count = Number(values.required_serial_numbers_count) || 0;
    processedValues.required_serial_numbers_count = count;
    if (count === 0) {
      processedValues.serial_number_pattern = null;
      processedValues.box_serial_number_delimiter = null;
    }

    // Note: For operator role, merchant_id is automatically injected by useOperatorProductService hook
    // Process photo
    if (values.photo && values.photo.length > 0) {
      const file = values.photo[0];
      if (file.originFileObj) {
        // New file uploaded - convert to base64
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(file.originFileObj);
        });
        processedValues.photo = { url: base64 };
      } else if (file.url) {
        // Existing photo - keep the URL
        processedValues.photo = { url: file.url };
      }
    } else {
      // No photo
      processedValues.photo = null;
    }

    return processedValues;
  };

  // 核心提交邏輯：呼叫 create/update 並回傳 response 給呼叫者決定後續行為
  const submitValues = async (values: any) => {
    const payload = await buildPayload(values);
    return isNew ? await service.create(payload) : await service.update(id as string, payload);
  };

  // SKU 預檢 + 阻擋：非 valid 就回傳 false，呼叫端不送 API
  const ensureSkuValid = async (values: any): Promise<boolean> => {
    if (!values.sku) return true;
    if (skuValidation.status === 'invalid') {
      message.error(skuValidation.message);
      return false;
    }
    if (skuValidation.status !== 'valid' || lastCheckedSkuRef.current !== values.sku.trim()) {
      const result = await checkSkuAvailability(values.sku);
      if (!result.valid) {
        message.error(result.message);
        return false;
      }
    }
    return true;
  };

  // 主要提交：成功後導向 show 頁（新建與編輯皆適用）
  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const skuOk = await ensureSkuValid(values);
      if (!skuOk) {
        setLoading(false);
        return;
      }

      const response = await submitValues(values);
      if (response.success) {
        message.success(t(`common.message.save_success`) as string);
        // 導向該筆記錄的 show 頁，避免返回快取的列表頁看不到新資料
        // React Router basename 會自動補上 /operator 或 /admin 前綴
        const recordId = response.data?.id || id;
        navigate(`/products/${recordId}`, { replace: true });
      } else {
        handleApiError(response, {
          t,
          fallbackMessage: t(`common.error.save_failed`)
        });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Submit error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      const skuOk = await ensureSkuValid(values);
      if (!skuOk) return;

      setSavingAndAdding(true);
      try {
        const response = await submitValues(values);
        if (response.success) {
          message.success(t(`common.message.save_success`) as string);
          // 重置 antd form 與外部 state（SKU 驗證、ShelfSelect initial option 等）
          form.resetFields();
          setSkuValidation({ status: 'idle', message: '' });
          lastCheckedSkuRef.current = null;
          setInitialShelfOption(null);
          // 重新套用新建模式預設值（預設溫層選第一個）
          if (temperatures && temperatures.length > 0) {
            form.setFieldsValue({ temperature: temperatures[0].value });
          }
          // activated 預設 true（與欄位 initialValue 一致）
          form.setFieldsValue({ activated: true, ec_stock_push_enabled: true });
        } else {
          handleApiError(response, {
            t,
            fallbackMessage: t(`common.error.save_failed`)
          });
        }
      } catch (error) {
        handleNetworkError(error, { t });
        console.error('Submit error:', error);
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  // 選擇替代商品
  const handleSelectSubstitute = (productId: string, selectedProduct: any) => {
    if (!productId) return;

    const currentSubstitutes = form.getFieldValue('product_substitutes_attributes') || [];

    // 檢查是否已存在
    if (currentSubstitutes.some((sub: any) => sub.candidate_id === productId && !sub._destroy)) {
      message.warning(t('shared.products.form.substitute_already_exists') as string);
      return;
    }

    const newSubstitute = {
      candidate_id: productId,
      sku: selectedProduct.sku,
      name: selectedProduct.name,
      priority: currentSubstitutes.length + 1
    };

    form.setFieldsValue({
      product_substitutes_attributes: [...currentSubstitutes, newSubstitute]
    });
    message.success(t('shared.products.form.substitute_added') as string);
  };

  // 給 ProductSelect（替代品搜尋）用：record's merchant 優先。Wrapper 在 edit 模式
  // 會把 record 的 merchant_id 經 useRecordMerchantSync 傳進 `merchantId` prop；
  // 暫態（response 已回但 wrapper 還沒 push 新 prop）時 fallback 到 loadedMerchantId。
  const targetMerchantId = loadedMerchantId ?? merchantId ?? null;

  const substitutes = (Form.useWatch('product_substitutes_attributes', form) || []) as any[];
  const excludedProductIds = useMemo(() => {
    const ids: Array<string | undefined> = [id]; // 排除自身
    substitutes.forEach((sub: any) => {
      if (!sub._destroy) ids.push(sub.candidate_id);
    });
    return ids.filter(Boolean) as string[];
  }, [id, substitutes]);

  // 「是否需要序號」由 count > 0 推導，但 Switch 本身用 local state；
  // 不能用 Form.useWatch 因為 AntD 5 的 useWatch 只在對應 Form.Item 掛載時才會 fire，
  // 而 InputNumber 只在 needSerialNumber=true 時才渲染 → chicken-and-egg，Switch 會卡死。
  const [needSerialNumber, setNeedSerialNumber] = useState(false);
  const ecSyncStockEnabled = Form.useWatch('ec_stock_push_enabled', form);
  // 訂閱整個 ec_push_settings_attributes 變動，讓 percentage 變動時可重新計算 >100 warning
  const ecPushSettingsWatch = Form.useWatch('ec_push_settings_attributes', form);
  const headerBasePath = role === 'operator' ? '/operator' : '/admin';

  // dataLoading：本元件還在打 GET show；merchantDataLoading：wrapper 還在把 currentMerchant
  // 切到 record's merchant。任一為 true 都先擋住 form，避免 brand / inventory_type 等
  // merchant-scoped 下拉先以前一個 merchant render，事後又跳值。
  if (dataLoading || merchantDataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
      </div>
    );
  }
  return (
    <div>
      <PageHeader
        basePath={headerBasePath}
        items={['products', 'products.list', { label: id ? t('common.button.edit') : t('common.button.create') }]}
      />
      {/* Tour 控制器：只在創建模式下渲染，獨立處理 watch 避免影響主表單性能 */}
      {!id && registerTour && (
        <ProductTourController
          form={form}
          registerTour={registerTour}
          refs={tourRefs}
          t={t}
        />
      )}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Collapse defaultActiveKey={['basic', 'unit']}>
            {/* 基本資訊 */}
            <Panel header={t(`shared.products.detail.sections.basic_info`)} key="basic">
              <Row gutter={[16, 16]}>
                <Col xs={24} md={18}>
                  {/* Identity Section */}
                  <Row gutter={[16, 0]}>
                    <Col xs={24} sm={12}>
                      <div ref={nameRef}>
                        <Form.Item
                          label={t(`models.product.name`)}
                          name="name"
                          rules={[{ required: true, message: t(`common.validation.required`) }]}
                        >
                          <Input data-testid="product-name-input" placeholder={t(`common.placeholder.please_enter`)} />
                        </Form.Item>
                      </div>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div ref={skuRef}>
                        <Form.Item
                          label={t(`models.product.sku`)}
                          name="sku"
                          rules={[{ required: true, message: t(`common.validation.required`) }]}
                          validateStatus={
                            skuValidation.status === 'checking' ? 'validating' :
                              skuValidation.status === 'invalid' ? 'error' :
                                skuValidation.status === 'valid' ? 'success' : undefined
                          }
                          help={skuValidation.status === 'invalid' ? skuValidation.message : undefined}
                          hasFeedback={skuValidation.status !== 'idle'}
                        >
                          <Input
                            data-testid="product-sku-input"
                            placeholder={t(`common.placeholder.please_enter`)}
                            onBlur={(e) => checkSkuAvailability(e.target.value)}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>

                  <Row gutter={[16, 0]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={t(`models.product.barcode`)}
                        name="barcode"
                        tooltip={t(`shared.products.form.barcode_tooltip`)}
                      >
                        <Input data-testid="product-barcode-input" placeholder={t(`common.placeholder.please_enter`)} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={t(`models.product.brand`)}
                        name="brand_id"
                      >
                        <Select
                          data-testid="product-brand-select"
                          showSearch
                          allowClear
                          placeholder={t(`shared.products.form.brand_placeholder`)}
                          options={brands}
                          onFocus={loadBrands}
                          loading={optionsLoading.brands}
                          filterOption={filterOption}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 0]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={t(`models.product.category`)}
                        name="product_category_id"
                      >
                        <Select
                          data-testid="product-category-select"
                          showSearch
                          placeholder={t(`shared.products.form.category_placeholder`)}
                          options={categories}
                          onFocus={loadCategories}
                          loading={optionsLoading.categories}
                          filterOption={filterOption}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div ref={temperatureRef}>
                        <Form.Item
                          label={t(`models.product.temperature`)}
                          name="temperature"
                          rules={[{ required: true, message: t(`common.validation.required`) }]}
                          tooltip={t(`shared.products.form.temperature_tooltip`)}
                        >
                          <Select
                            data-testid="product-temperature-select"
                            showSearch
                            placeholder={t(`shared.products.form.temperature_placeholder`)}
                            options={temperatures}
                            filterOption={(input, option) =>
                              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>

                  <Form.Item
                    label={t(`models.product.secondary_barcodes`)}
                    name="secondary_barcodes"
                    tooltip={t(`shared.products.form.secondary_barcodes_hint`)}
                  >
                    <TextArea
                      data-testid="product-secondary-barcodes-input"
                      rows={2}
                      placeholder={t(`shared.products.form.secondary_barcodes_placeholder`)}
                    />
                  </Form.Item>

                  {/* 專屬倉：default_shelf_id 內嵌在基礎資訊（與 SKU / Barcode 同區） */}
                  {role === 'operator' && isStorageModeDedicated && (
                    <Row gutter={[16, 0]}>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item
                          label={t(`models.product.default_shelf`)}
                          name="default_shelf_id"
                        >
                          <ShelfSelect
                            placeholder={t('shared.products.form.default_shelf_placeholder', { defaultValue: '請搜尋並選擇儲位' })}
                            initialOption={initialShelfOption}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  <Divider />

                  {/* Management Section */}
                  <Row gutter={[16, 0]}>
                    <Col xs={12} md={6}>
                      <Form.Item
                        label={t(`models.product.activated`)}
                        name="activated"
                        valuePropName="checked"
                        initialValue={true}
                        tooltip={t(`shared.products.form.activated_tooltip`)}
                      >
                        <Switch data-testid="product-activated-switch" />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Item
                        label={t(`models.product.oversea_imported`)}
                        name="oversea_imported"
                        valuePropName="checked"
                        tooltip={t(`shared.products.form.oversea_imported_tooltip`)}
                      >
                        <Switch data-testid="product-oversea-imported-switch" />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Item
                        label={t(`models.product.manufacturing_date_required`)}
                        name="manufacturing_date_required"
                        valuePropName="checked"
                        tooltip={t(`shared.products.form.manufacturing_date_required_tooltip`)}
                      >
                        <Switch data-testid="product-mfg-date-required-switch" />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Item
                        label={t(`models.product.batch_management_required`)}
                        name="batch_management_required"
                        valuePropName="checked"
                        tooltip={t(`shared.products.form.batch_management_required_tooltip`)}
                      >
                        <Switch data-testid="product-batch-mgmt-required-switch" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 0]}>
                    <Col xs={24} sm={12}>
                      <div ref={validityDaysRef}>
                        <Form.Item
                          label={t(`models.product.validity_days`)}
                          name="validity_days"
                          tooltip={t(`shared.products.form.validity_days_hint`)}
                        >
                          <InputNumber
                            data-testid="product-validity-days-input"
                            min={0}
                            style={{ width: '100%' }}
                            placeholder={t(`shared.products.form.validity_days_placeholder`)}
                            addonAfter={t('common.unit.days')}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>

                  <Divider />

                  {/* Serial Number Section — Switch 由 required_serial_numbers_count > 0 推導（無獨立 boolean 欄位） */}
                  <Row gutter={[16, 0]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={t(`shared.products.form.needs_serial_number_label`)}
                        tooltip={t(`shared.products.form.needs_serial_number_hint`)}
                      >
                        <Switch
                          checked={needSerialNumber}
                          onChange={(checked) => {
                            setNeedSerialNumber(checked);
                            if (checked) {
                              const existing = Number(form.getFieldValue('required_serial_numbers_count')) || 0;
                              form.setFieldValue('required_serial_numbers_count', existing > 0 ? existing : 1);
                            } else {
                              form.setFieldsValue({
                                required_serial_numbers_count: 0,
                                serial_number_pattern: null,
                                box_serial_number_delimiter: null,
                              });
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                    {needSerialNumber && (
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={t(`models.product.required_serial_numbers_count`)}
                          name="required_serial_numbers_count"
                          tooltip={t(`shared.products.form.required_serial_numbers_count_hint`)}
                          rules={[
                            {
                              type: 'number',
                              min: 1,
                              message: t(`shared.products.form.required_serial_numbers_count_min`),
                            },
                          ]}
                        >
                          <InputNumber
                            min={1}
                            style={{ width: '100%' }}
                            placeholder={t(`shared.products.form.required_serial_numbers_count_placeholder`)}
                          />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                  {needSerialNumber && (
                    <div style={{ background: '#f5f5f5', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                      <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={t(`models.product.box_serial_number_delimiter`)}
                            name="box_serial_number_delimiter"
                            tooltip={t(`shared.products.form.box_serial_number_delimiter_hint`)}
                          >
                            <Input placeholder={t(`shared.products.form.box_serial_number_delimiter_placeholder`)} />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            label={t(`models.product.serial_number_pattern`)}
                            name="serial_number_pattern"
                            tooltip={t(`shared.products.form.serial_number_pattern_hint`)}
                            rules={[
                              {
                                validator: async (_, value) => {
                                  if (!value) return Promise.resolve();
                                  try {
                                    new RegExp(value);
                                    return Promise.resolve();
                                  } catch (e) {
                                    return Promise.reject(new Error(t(`shared.products.form.serial_number_pattern_invalid`) as string));
                                  }
                                }
                              }
                            ]}
                          >
                            <Input placeholder={t(`shared.products.form.serial_number_pattern_placeholder`)} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  )}

                  <Divider />

                  {/* EC Stock Push Section */}
                  <Row gutter={[16, 0]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={t(`models.product.ec_stock_push_enabled`)}
                        name="ec_stock_push_enabled"
                        valuePropName="checked"
                        initialValue={true}
                        tooltip={t(`shared.products.form.ec_stock_push_enabled_tooltip`)}
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                  </Row>
                  {ecSyncStockEnabled && (
                    <Row>
                      <Col span={24}>
                        <Form.Item
                          label={t(`shared.products.form.ec_push_settings_label`)}
                          tooltip={t(`shared.products.form.ec_push_settings_tooltip`)}
                        >
                          <Form.List name="ec_push_settings_attributes">
                            {(fields, { add, remove }) => {
                              // 同時讀 watch（觸發 re-render）與 form 值（確保 SSR 初始 mount 拿得到）
                              const rows = ((ecPushSettingsWatch as any[]) ?? (form.getFieldValue('ec_push_settings_attributes') || [])) as any[];
                              const takenIds = new Set(rows.map(r => r?.ec_account_id).filter(Boolean));
                              const availableCandidates = ecPushAccountCandidates.filter(
                                c => !takenIds.has(c.ec_account_id)
                              );
                              const hasCandidates = ecPushAccountCandidates.length > 0;

                              if (!hasCandidates) {
                                return (
                                  <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={t(`shared.products.form.ec_push_settings_no_candidates`)}
                                  />
                                );
                              }

                              // 計算所有電商帳號加總比例（例外 row 用 override / 預設帳號用 account_stock_percentage）
                              // 超過 100% 代表此商品庫存被分配到各通路的總額超量，會有超賣風險
                              const rowsByAccountId = new Map(
                                rows.filter(r => r?.ec_account_id).map(r => [r.ec_account_id, r])
                              );
                              const totalPct = ecPushAccountCandidates.reduce((sum, c) => {
                                const exceptionRow = rowsByAccountId.get(c.ec_account_id);
                                if (exceptionRow) {
                                  if (exceptionRow.enabled === false) return sum;
                                  const v = Number(exceptionRow.stock_percentage_override);
                                  return sum + (Number.isFinite(v) ? v : 0);
                                }
                                return sum + (Number(c.account_stock_percentage) || 0);
                              }, 0);
                              const totalOver100 = totalPct > 100;

                              return (
                                <div style={{ border: '1px solid #f0f0f0', borderRadius: 8 }}>
                                  {totalOver100 && (
                                    <Alert
                                      type="warning"
                                      showIcon
                                      style={{ margin: '8px 16px' }}
                                      message={t(`shared.products.form.ec_push_settings_warning_total_over_100`, {
                                        total: totalPct.toFixed(3).replace(/\.?0+$/, '')
                                      })}
                                    />
                                  )}
                                  {fields.length === 0 ? (
                                    <div
                                      style={{
                                        padding: '16px',
                                        textAlign: 'center',
                                        fontSize: 13,
                                        color: '#999'
                                      }}
                                    >
                                      {t(`shared.products.form.ec_push_settings_empty`)}
                                    </div>
                                  ) : (
                                    <>
                                      <Row
                                        align="middle"
                                        style={{
                                          padding: '8px 16px',
                                          background: '#fafafa',
                                          borderBottom: '1px solid #f0f0f0',
                                          fontSize: 12,
                                          color: '#999'
                                        }}
                                      >
                                        <Col flex="auto">{t(`shared.products.form.ec_push_settings_col_account`)}</Col>
                                        <Col flex="80px" style={{ textAlign: 'center' }}>
                                          {t(`shared.products.form.ec_push_settings_col_enabled`)}
                                        </Col>
                                        <Col flex="160px" style={{ textAlign: 'right' }}>
                                          {t(`shared.products.form.ec_push_settings_col_ratio`)}
                                        </Col>
                                        <Col flex="40px" />
                                      </Row>
                                      {fields.map((field, idx) => {
                                        const row = rows[field.name] || form.getFieldValue(['ec_push_settings_attributes', field.name]) || {};
                                        const rowEnabled = row.enabled !== false;
                                        return (
                                          <Row
                                            align="middle"
                                            key={field.key}
                                            style={{
                                              padding: '10px 16px',
                                              borderBottom: idx === fields.length - 1 ? 'none' : '1px solid #f5f5f5'
                                            }}
                                          >
                                            <Col flex="auto" style={{ fontSize: 14, color: '#333', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                                              {row.ec_account_module_name && (
                                                <Tag color="blue" style={{ marginRight: 0 }}>{row.ec_account_module_name}</Tag>
                                              )}
                                              <span>{row.ec_account_name}</span>
                                              {row.inventory_type_name && (
                                                <Tag style={{ marginRight: 0 }}>{row.inventory_type_name}</Tag>
                                              )}
                                              {row.secondary_inventory_type_name && (
                                                <Tag style={{ marginRight: 0 }}>{row.secondary_inventory_type_name}</Tag>
                                              )}
                                            </Col>
                                            <Col flex="80px" style={{ textAlign: 'center' }}>
                                              <Form.Item
                                                {...field}
                                                name={[field.name, 'enabled']}
                                                valuePropName="checked"
                                                noStyle
                                              >
                                                <Checkbox />
                                              </Form.Item>
                                            </Col>
                                            <Col flex="160px">
                                              <Form.Item
                                                {...field}
                                                name={[field.name, 'stock_percentage_override']}
                                                // 取消「推送」時，比例欄位無意義，因此不必填、也不顯示 warning
                                                rules={rowEnabled ? [{ required: true, message: t(`common.validation.required`) as string }] : []}
                                                style={{ marginBottom: 0 }}
                                              >
                                                <InputNumber
                                                  min={0}
                                                  step={0.001}
                                                  precision={3}
                                                  style={{ width: '100%' }}
                                                  addonAfter="%"
                                                  disabled={!rowEnabled}
                                                />
                                              </Form.Item>
                                            </Col>
                                            <Col flex="40px" style={{ textAlign: 'right' }}>
                                              <Button
                                                type="text"
                                                size="small"
                                                icon={<DeleteOutlined />}
                                                onClick={() => remove(field.name)}
                                                aria-label={t('common.delete') as string}
                                              />
                                            </Col>
                                          </Row>
                                        );
                                      })}
                                    </>
                                  )}
                                  {availableCandidates.length > 0 && (
                                    <div
                                      style={{
                                        padding: '8px 16px',
                                        borderTop: fields.length === 0 ? 'none' : '1px solid #f0f0f0'
                                      }}
                                    >
                                      <Dropdown
                                        trigger={['click']}
                                        menu={{
                                          items: availableCandidates.map(c => ({
                                            key: c.ec_account_id,
                                            label: (
                                              <span>
                                                {c.ec_account_module_name && (
                                                  <Tag color="blue" style={{ marginRight: 6 }}>{c.ec_account_module_name}</Tag>
                                                )}
                                                {c.ec_account_name}
                                              </span>
                                            )
                                          })),
                                          onClick: ({ key }) => {
                                            const candidate = ecPushAccountCandidates.find(c => c.ec_account_id === key);
                                            if (!candidate) return;
                                            add({
                                              ec_account_id: candidate.ec_account_id,
                                              ec_account_name: candidate.ec_account_name,
                                              ec_account_module_name: candidate.ec_account_module_name,
                                              account_stock_percentage: candidate.account_stock_percentage,
                                              inventory_type_name: candidate.inventory_type_name,
                                              secondary_inventory_type_name: candidate.secondary_inventory_type_name,
                                              enabled: true,
                                              // 預填帳號預設值，使用者再調整（百分比必填）
                                              stock_percentage_override: candidate.account_stock_percentage ?? null
                                            });
                                          }
                                        }}
                                      >
                                        <Button type="dashed" block icon={<PlusOutlined />}>
                                          {t(`shared.products.form.ec_push_settings_add`)}
                                        </Button>
                                      </Dropdown>
                                    </div>
                                  )}
                                </div>
                              );
                            }}
                          </Form.List>
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    label={t(`models.product.photo`)}
                    name="photo"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => {
                      if (Array.isArray(e)) {
                        return e;
                      }
                      return e?.fileList;
                    }}
                  >
                    <Upload
                      listType="picture-card"
                      maxCount={1}
                      beforeUpload={() => false}
                      accept="image/*"
                    >
                      <div>
                        <UploadOutlined />
                        <div style={{ marginTop: 8 }}>{t('common.button.upload')}</div>
                      </div>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>

              {/* 自定義屬性 */}
              {productAttributeDefinitions.length > 0 && (
                <>
                  <Divider orientation="left" style={{ marginTop: 24 }}>
                    <Typography.Text type="secondary">{t(`models.product.custom_attributes`)}</Typography.Text>
                  </Divider>
                  <Alert
                    message={t('shared.products.form.custom_attributes_hint')}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  <Row gutter={[16, 0]}>
                    {productAttributeDefinitions.map((def: any) => {
                      const fieldName = ['custom_attributes', def.name];
                      // 優先使用 i18n 翻譯，找不到就用資料庫的 label
                      const label = t(`models.product_attribute.${def.name}`, { defaultValue: def.label });
                      const isBoolean = def.data_type === 'boolean';
                      let inputComponent;

                      switch (def.data_type) {
                        case 'number':
                          inputComponent = (
                            <InputNumber style={{ width: '100%' }} placeholder={label as string} />
                          );
                          break;
                        case 'date':
                          inputComponent = (
                            <Input type="date" placeholder={label as string} />
                          );
                          break;
                        case 'boolean':
                          // Boolean is always binary; Schema 層 (ProductAttributeDefinition)
                          // 已禁止 boolean + required=true，因此這裡永遠用 Switch。
                          inputComponent = <Switch />;
                          break;
                        case 'select':
                          inputComponent = (
                            <Select
                              placeholder={label as string}
                              allowClear
                              options={(def.options || []).map((opt: string) => ({ label: opt, value: opt }))}
                            />
                          );
                          break;
                        default: // text
                          inputComponent = (
                            <Input placeholder={label as string} />
                          );
                      }

                      return (
                        <Col xs={24} sm={12} md={8} key={def.id}>
                          <Form.Item
                            label={label}
                            name={fieldName}
                            rules={def.required ? [{ required: true, message: t('common.validation.required') }] : []}
                            valuePropName={isBoolean ? 'checked' : 'value'}
                            initialValue={isBoolean ? false : undefined}
                          >
                            {inputComponent}
                          </Form.Item>
                        </Col>
                      );
                    })}
                  </Row>
                </>
              )}
              {/* 備註 */}
              <Row gutter={[16, 0]} style={{ marginTop: 16 }}>
                <Col xs={24}>
                  <Form.Item
                    label={t(`models.product.note`)}
                    name="note"
                  >
                    <TextArea data-testid="product-note-input" rows={3} placeholder={t(`shared.products.form.note_placeholder`)} />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
            {/* 單位資訊 */}
            <Panel header={t(`shared.products.detail.sections.unit_info`)} key="unit">
              <Alert
                message={t(`shared.products.form.unit_info_description`)}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label={t(`models.product.small_unit_name`)}
                    name="small_unit_name"
                    tooltip={t(`shared.products.form.small_unit_name_tooltip`)}
                    rules={[{ pattern: /^(?!\d)/, message: t(`shared.products.form.unit_name_cannot_start_with_digit`) }]}
                  >
                    <Input data-testid="product-small-unit-name-input" placeholder={t(`shared.products.form.small_unit_name_placeholder`)} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 0]}>
                <Col xs={12} md={6}>
                  <Form.Item
                    label={t(`models.product.length`)}
                    name="length"
                  >
                    <InputNumber data-testid="product-length-input" min={0} style={{ width: '100%' }} placeholder="cm" />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item
                    label={t(`models.product.width`)}
                    name="width"
                  >
                    <InputNumber data-testid="product-width-input" min={0} style={{ width: '100%' }} placeholder="cm" />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item
                    label={t(`models.product.height`)}
                    name="height"
                  >
                    <InputNumber data-testid="product-height-input" min={0} style={{ width: '100%' }} placeholder="cm" />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item
                    label={t(`models.product.weight_kg`)}
                    name="weight_kg"
                  >
                    <InputNumber data-testid="product-weight-kg-input" min={0} style={{ width: '100%' }} placeholder="kg" />
                  </Form.Item>
                </Col>
              </Row>
              <div style={{ borderTop: '1px solid #f0f0f0', margin: '16px 0' }} />
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label={t(`models.product.mid_unit_name`)}
                    name="mid_unit_name"
                    tooltip={t(`shared.products.form.mid_unit_name_tooltip`)}
                    rules={[{ pattern: /^(?!\d)/, message: t(`shared.products.form.unit_name_cannot_start_with_digit`) }]}
                  >
                    <Input data-testid="product-mid-unit-name-input" placeholder={t(`shared.products.form.mid_unit_name_placeholder`)} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label={t(`models.product.small_quantity_per_mid_unit`)}
                    name="small_quantity_per_mid_unit"
                    tooltip={t(`shared.products.form.small_quantity_per_mid_unit_tooltip`)}
                  >
                    <InputNumber data-testid="product-small-qty-per-mid-input" min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label={t(`models.product.mid_unit_barcode`)}
                    name="mid_unit_barcode"
                  >
                    <Input data-testid="product-mid-unit-barcode-input" placeholder={t(`shared.products.form.mid_unit_barcode_placeholder`)} />
                  </Form.Item>
                </Col>
              </Row>
              <div style={{ borderTop: '1px solid #f0f0f0', margin: '16px 0' }} />
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label={t(`models.product.large_unit_name`)}
                    name="large_unit_name"
                    tooltip={t(`shared.products.form.large_unit_name_tooltip`)}
                    rules={[{ pattern: /^(?!\d)/, message: t(`shared.products.form.unit_name_cannot_start_with_digit`) }]}
                  >
                    <Input data-testid="product-large-unit-name-input" placeholder={t(`shared.products.form.large_unit_name_placeholder`)} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <div ref={largeUnitSmallQuantityRef}>
                    <Form.Item
                      label={t(`models.product.small_quantity_per_large_unit`)}
                      name="small_quantity_per_large_unit"
                      tooltip={t(`shared.products.form.small_quantity_per_large_unit_tooltip`)}
                    >
                      <InputNumber data-testid="product-small-qty-per-large-input" min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label={t(`models.product.large_unit_barcode`)}
                    name="large_unit_barcode"
                  >
                    <Input data-testid="product-large-unit-barcode-input" placeholder={t(`shared.products.form.large_unit_barcode_placeholder`)} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 0]}>
                <Col xs={12} md={6}>
                  <Form.Item
                    label={t(`models.product.box_length`)}
                    name="box_length"
                  >
                    <InputNumber data-testid="product-box-length-input" min={0} style={{ width: '100%' }} placeholder="cm" />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item
                    label={t(`models.product.box_width`)}
                    name="box_width"
                  >
                    <InputNumber data-testid="product-box-width-input" min={0} style={{ width: '100%' }} placeholder="cm" />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item
                    label={t(`models.product.box_height`)}
                    name="box_height"
                  >
                    <InputNumber data-testid="product-box-height-input" min={0} style={{ width: '100%' }} placeholder="cm" />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item
                    label={t(`models.product.box_weight_kg`)}
                    name="box_weight_kg"
                  >
                    <InputNumber data-testid="product-box-weight-kg-input" min={0} style={{ width: '100%' }} placeholder="kg" />
                  </Form.Item>
                </Col>
              </Row>
              <div style={{ borderTop: '1px solid #f0f0f0', margin: '16px 0' }} />
              <PalletConfigFields form={form} t={t} />
            </Panel>
            {/* 儲位資訊（僅 Operator + 動態倉可見；
                專屬倉已內嵌至基礎資訊；虛擬倉無儲位故不顯示；
                Admin/貨主不涉及倉庫內部儲位細節） */}
            {role === 'operator' && isStorageModeDynamic && (
              <Panel header={t(`shared.products.detail.sections.shelves_info`)} key="shelves">
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      label={t(`models.product.default_shelf`)}
                      name="default_shelf_id"
                    >
                      <ShelfSelect
                        placeholder={t('shared.products.form.default_shelf_placeholder', { defaultValue: '請搜尋並選擇儲位' })}
                        initialOption={initialShelfOption}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Panel>
            )}
            {/* 替代品資訊 */}
            <Panel header={t(`shared.products.detail.sections.substitutes_info`)} key="substitutes">
              <Alert
                message={t('shared.products.form.substitutes_hint')}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Form.List name="product_substitutes_attributes">
                {(fields, { add, remove, move }) => (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <Card key={key} size="small" type="inner" title={t('shared.products.form.substitute_index', { index: index + 1 })}>
                        <Row gutter={16} align="middle">
                          <Col span={18}>
                            <Row gutter={16}>
                              <Col span={10}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'sku']}
                                  label={t('models.product.sku')}
                                >
                                  <Input disabled />
                                </Form.Item>
                              </Col>
                              <Col span={14}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'name']}
                                  label={t('models.product.name')}
                                >
                                  <Input disabled />
                                </Form.Item>
                              </Col>
                            </Row>
                            {/* Hidden ID, Candidate ID and Priority */}
                            <Form.Item {...restField} name={[name, 'id']} hidden><Input /></Form.Item>
                            <Form.Item {...restField} name={[name, 'candidate_id']} hidden><Input /></Form.Item>
                            <Form.Item {...restField} name={[name, 'priority']} hidden><InputNumber /></Form.Item>
                          </Col>
                          <Col span={6} style={{ textAlign: 'right' }}>
                            <Space>
                              <Button
                                type="text"
                                icon={<ArrowUpOutlined />}
                                disabled={index === 0}
                                onClick={() => move(index, index - 1)}
                              />
                              <Button
                                type="text"
                                icon={<ArrowDownOutlined />}
                                disabled={index === fields.length - 1}
                                onClick={() => move(index, index + 1)}
                              />
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => remove(name)}
                              />
                            </Space>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                    <ProductSelect
                      productService={service}
                      merchantId={targetMerchantId}
                      excludeIds={excludedProductIds}
                      placeholder={t('shared.products.button.add_substitute')}
                      onSelect={handleSelectSubstitute}
                      value={null}
                    />
                  </div>
                )}
              </Form.List>
            </Panel>
            {/* 出口資訊 */}
            <Panel header={t(`shared.products.detail.sections.export_info`)} key="export">
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label={t(`models.product.customs_declare_product_title`)}
                    name="customs_declare_product_title"
                  >
                    <Input data-testid="product-customs-title-input" placeholder={t(`shared.products.form.customs_declare_product_title_placeholder`)} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label={t(`models.product.manufacturer_country`)}
                    name="manufacturer_country"
                  >
                    <Input data-testid="product-manufacturer-country-input" placeholder={t(`shared.products.form.manufacturer_country_placeholder`)} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label={t(`models.product.hs_code`)}
                    name="hs_code"
                  >
                    <Input data-testid="product-hs-code-input" placeholder={t(`shared.products.form.hs_code_placeholder`)} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label={t(`models.product.material`)}
                    name="material"
                  >
                    <Input data-testid="product-material-input" placeholder={t(`shared.products.form.material_placeholder`)} />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          </Collapse>
          <FloatingActionBarSpacer />
        </Form>

      <FloatingActionBar
        role={role}
        onCancel={goBack}
        onSubmit={() => form.submit()}
        loading={loading}
        disabled={savingAndAdding}
        submitRef={saveButtonRef}
        submitRequiredPermission="products:write"
        extraButtons={isNew ? (
          <Can requiredPermission="products:write">
            <Button
              loading={savingAndAdding}
              disabled={loading}
              onClick={handleSaveAndAddAnother}
            >
              {t('common.button.save_and_add_another')}
            </Button>
          </Can>
        ) : null}
        submitText={t(isNew ? 'common.button.create' : 'common.button.save')}
      />
    </div>
  );
};

export default ProductEdit;
